<template>
    <div class="questions">
        <div class="title">
            <h2 class="acenter">{{ $l10n.__('Des questions ?') }}</h2>
            <Underline class="underline-svg" />
        </div>
        <el-collapse>
            <el-collapse-item
                v-for="question in questions"
                :key="question.title"
                :title="question.title"
            >
                <div v-html="question.text" />
            </el-collapse-item>
        </el-collapse>
    </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import { offersPath, getGoSellsyUrl } from 'Config/authenticated/goSellsyPaths';
import Underline from 'vcomponents/icons/IconUnderlineBlue.vue';

const user = createNamespacedHelpers('user');

export default {
    components: {
        Underline,
    },
    data() {
        return {
            questions: [
                {
                    title: this.$l10n.__(
                        'Dois-je saisir mes coordonnées bancaires pour essayer Sellsy ?',
                    ),
                    text: this.$l10n.__(
                        'Non, vos 15 jours d’essai sont totalement gratuits, et l’inscription ne nécessite aucune carte bancaire.',
                    ),
                },
                {
                    title: this.$l10n.__('Que se passe-t-il à la fin de mon essai ?'),
                    text: this.$l10n.__(
                        'Pendant vos 15 jours d’essai gratuit, vous avez la possibilité de souscrire à tout moment à l’abonnement Sellsy. Au-delà de ce délai, un premier paiement sera exigé pour accéder à votre compte.',
                    ),
                },
                {
                    title: this.$l10n.__(
                        'Je préfère avoir une démonstration de Sellsy, comment cela se passe-t-il ?',
                    ),
                    text: this.$l10n.__(
                        'Pas de problème, vous pouvez demander une démonstration via %(startLink)s ce formulaire%(endLink)s, notre équipe vous recontactera pour programmer un rendez-vous en ligne de 30 minutes avec l’un de nos experts.',
                        {
                            startLink: `<a target='_blank' class='semibold' href='${getGoSellsyUrl(
                                this.userLang,
                                offersPath,
                            )}'>`,
                            endLink: `</a>`,
                        },
                    ),
                },
                {
                    title: this.$l10n.__(
                        "Puis-je ajouter des utilisateurs en cours d'abonnement ?",
                    ),
                    text: this.$l10n.__(
                        'Bien sûr ! Depuis les réglages, créez des accès à vos collaborateurs en renseignant simplement leur adresse email. Ils recevront l’invitation instantanément.',
                    ),
                },
                {
                    title: this.$l10n.__('Où mes données sont-elles stockées ?'),
                    text: this.$l10n.__(
                        'Sellsy est hébergé sur notre propre plateforme dédiée, dans un datacenter disposant de connexions redondantes avec les principaux opérateurs mondiaux. Nos serveurs sont gérés par des professionnels reconnus de l’hébergement, Claranet. L’ensemble de vos données est sauvegardé chaque nuit, et est stocké en France.',
                    ),
                },
                {
                    title: this.$l10n.__('Le support est-il compris dans mon abonnement ?'),
                    text: this.$l10n.__(
                        'Bien évidemment, nos équipes sont disponibles de 9h à 18h, du lundi au vendredi, pour vous répondre via notre chat (accessible en bas à gauche de votre écran). Vous pouvez également nous envoyer un mail à %(startlink)s contact@sellsy.com%(endLink)s.',
                        {
                            startlink: `<a target='_blank' class='semibold' href='mailto:contact@sellsy.com'>`,
                            endLink: `</a>`,
                        },
                    ),
                },
                {
                    title: this.$l10n.__('A quel moment dois-je régler mon abonnement ?'),
                    text: this.$l10n.__(
                        'Sellsy vous propose deux types de règlement par défaut : mensuel ou annuel. Choisissez l’option qui vous convient au moment de valider votre abonnement.',
                    ),
                },
                {
                    title: this.$l10n.__('Puis-je donner un accès à mon expert comptable ?'),
                    text: this.$l10n.__(
                        'L’accès expert comptable est inclus dans votre offre, en plus du nombre d’utilisateurs que vous avez choisi. Vous pourrez directement inviter votre expert comptable depuis les réglages de votre compte Sellsy.',
                    ),
                },
            ],
        };
    },
    computed: {
        ...user.mapState(['locale']),
        userLang() {
            const acceptedLang = ['en', 'fr'];
            const userLocale = this.locale.split('_')[0];
            return acceptedLang.includes(userLocale) ? userLocale : 'fr';
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~@sellsy/styleguide/src/_variables';

.questions {
    margin: 0 auto;
    padding: 0 0 100px;
    max-width: 792px;
    width: 100%;
    .title {
        align-items: center;
        display: flex;
        flex-direction: column;
        margin: 80px 0 40px;
    }
    h2 {
        color: $secondary-blue;
        font-family: $font-2;
        font-size: 40px;
        font-weight: 400;
        margin: 0 0 4px;
    }
    &::v-deep {
        .el-collapse {
            border-bottom: 0;
            border-top: 0;
            &-item {
                border-bottom: 1px solid $gray-05;
                &:last-child {
                    margin-bottom: 0;
                }
                &__wrap {
                    border-bottom: 0;
                }
                &__header {
                    border-bottom: 0;
                    color: $secondary-blue;
                    font-family: $font-2;
                    font-size: 18px;
                    font-weight: 400;
                    height: auto;
                    line-height: 1.07;
                    padding: 24px 24px 24px 0;
                    position: relative;
                    &:before,
                    &:after {
                        content: '';
                        background: $primary-50;
                        height: 2px;
                        position: absolute;
                        right: 5px;
                        top: 50%;
                        transform: translateY(-50%);
                        transition: transform 200ms ease-in-out;
                        width: 16px;
                    }
                    &:after {
                        transform: rotate(90deg) translateX(-1px);
                        will-change: transform;
                    }
                    &.is-active {
                        &:after {
                            transform: rotate(0deg) translateY(-1px);
                        }
                    }
                }
                &__content {
                    color: $gray-80;
                    font-size: 16px;
                    line-height: 24px;
                    padding-bottom: 24px;
                }
                &__arrow {
                    display: none;
                }
            }
        }
    }
}
</style>
